import { Injectable } from '@angular/core';
import { IChannelSettingModel, IChannelItem } from '../../../shared/models';
import { cloneDeep, isEmpty, minBy, maxBy, findIndex } from 'lodash';
import { IInputDataWell, IWellOptions, BaseChartService } from '../../../shared/services/charts';
import { DefaultColor } from '../../../shared/helpers/app.constants';
import { Helper } from '../../../shared/helpers/helper';
import { IWellItemPaging } from '../../../shared/models/well.model';
import { CompareDataTreatmentsStored } from '../compare-data-treatments-stored';
import { ITreatmentChartRequestOption, ChartsService } from '../../../shared/services';
import { Observable, forkJoin, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class CompareMultipleWellService {
  private readonly wellListSubject: Subject<IWellItemPaging[]> = new Subject();
  public wellList$ = this.wellListSubject.asObservable();

  constructor(
    private chartsService: ChartsService,
    private baseChartService: BaseChartService
  ) { }

  setWellList(wells) {
    this.wellListSubject.next(wells);
  }

  unsetWellList() {
    this.wellListSubject.next(null);
  }

  get wellListLength$() {
    return this.wellList$.pipe(map(res => {
      if (res) return res.length;
      return;
    }));
  }

  get selectedTreatmentsMultiWells$() {
    return this.wellList$.pipe(map(wells => {
      let result = [];
      if (wells) {
        wells.forEach(well => {
          well.selectedTreatmentList.map(treatment => {
            const treatmentData = well.treatmentList.find(trm => trm.id === treatment.id);
            if (treatmentData) treatment.treatmentNumber = treatmentData.treatmentNumber;
            return treatment;
          });
          result = result.concat([...well.selectedTreatmentList]);
        });
      }
      return result;
    }));
  }

  get singleWell$() {
    return this.wellList$.pipe(map(res => {
      if (res && res.length === 1) {
        return res[0];
      }
      return;
    }));
  }

  get selectedTreatmentsSingleWell$() {
    return this.wellList$.pipe(map(res => {
      if (res && res.length === 1) {
        return res[0].selectedTreatmentList.map(treatment => {
          const treatmentData = res[0].treatmentList.find(trm => trm.id === treatment.id);
          if (treatmentData) treatment.treatmentNumber = treatmentData.treatmentNumber;
          return treatment;
        });
      }
      return;
    }));
  }

  populateChannelSettingsByWellData(originChannelSettings: IChannelSettingModel, realtimeChannels: IChannelItem[], realtimeTreatmentChannels: any[], inputData: IInputDataWell[]) {
    // use case mapped difference channels with a same name in difference wells
    // have to find the correct cName for orignal channel in every well
    const filterRealTreatment = (well, wellData, channelSettingItem) => {
      if (well.wellId !== wellData.wellId) return false;
      if (well.treatmentNumber < 0 && wellData.treatmentNumber > 0) return false;
      if (well.treatmentNumber > 0 && wellData.treatmentNumber < 0) return false;
      for (const channel of well.channels) {
        if (channel.name === channelSettingItem.name) return true;
      }
      return false;
    };

    // const filterRealtimeChannnel = (channel, wellData, channelSettingItem) => {
    //   if (channel.name !== channelSettingItem.channelName) return false;
    //   if (channel.wellId === wellData.wellId) return true;
    //   if (!channel.channelMappedGroup || !channel.channelMappedGroup.length) return false;
    //   const channelExist = channel.channelMappedGroup.find(item => item.wellId === wellData.wellId);
    //   if (!channelExist) return false;
    //   return true;
    // }

    if (!isEmpty(inputData)) {
      const mappedInputData = [...inputData];
      mappedInputData.forEach(wellData => {
        // update channels in channelSettings by well
        wellData.channelSettings = cloneDeep(originChannelSettings);
        for (const key in wellData.channelSettings) {
          if (wellData.channelSettings.hasOwnProperty(key)) {
            const setting = wellData.channelSettings[key];
            if (setting && !isEmpty(setting.channels)) {
              setting.channels.forEach(channelSettingItem => {
                if (channelSettingItem) {
                  const realTreatmentChannel = realtimeTreatmentChannels.find(well => filterRealTreatment(well, wellData, channelSettingItem));
                  if (realTreatmentChannel) {
                    const hasTreatmentChannel = realTreatmentChannel.channels.find(channel => channel.name === channelSettingItem.channelName);
                    if (hasTreatmentChannel) {
                      // const originChannel = realtimeChannels.find(channel => {
                      //   return channel.name === channelSettingItem.channelName && channel.wellId === wellData.wellId;
                      // });
                      let originChannel: any;
                      for (const channel of realtimeChannels) {
                        if (channel.name !== channelSettingItem.channelName) continue;
                        if (channel.wellId === wellData.wellId) {
                          originChannel = channel;
                          break;
                        }
                        if (!channel.channelMappedGroup || !channel.channelMappedGroup.length) continue;
                        const channelExist = channel.channelMappedGroup.find(item => item.wellId === wellData.wellId);
                        if (!channelExist) continue;
                        originChannel = cloneDeep(channel);
                        originChannel.cName = channelExist.cName;
                        originChannel.dataType = channelExist.dataType;
                        originChannel.measureType = channelExist.measureType;
                        originChannel.originalName = channelExist.originalName;
                        originChannel.treatmentId = channelExist.treatmentId;
                        originChannel.unit = channelExist.unit;
                        originChannel.wellId = channelExist.wellId;
                        break;
                      }
                      // update correct cName in channel setting
                      if (originChannel && originChannel.cName) {
                        channelSettingItem.cName = originChannel.cName;
                      } else {
                        channelSettingItem.cName = '';
                      }
                    }
                  } else {
                    channelSettingItem.cName = '';
                  }
                }
              });
            }
          }
        }
      });
      return mappedInputData;
    } else {
      return inputData;
    }
  }

  findDateRangeData(wellList: IWellItemPaging[], toISOString?: boolean) {
    // find data ranges by treatment's timeStart
    const result = {
      startTime: null,
      endTime: null
    };
    if (!isEmpty(wellList)) {
      const minTimeStartByWell = [];
      const maxTimeStartByWell = [];
      wellList.forEach(well => {
        let minItem: any = minBy(well.treatmentList, 'timeStart');
        let maxItem: any = maxBy(well.treatmentList, 'timeStart');
        let tzTimeZone: string;
        if (!well.tzTimeZone) tzTimeZone = Helper.lookupTimeZone(well.country, well.timeZone);
        else tzTimeZone = well.tzTimeZone;

        if (minItem) {
          minItem.tzTimeZone = tzTimeZone;
          minTimeStartByWell.push(minItem);
        }
        if (maxItem) {
          maxItem.tzTimeZone = tzTimeZone;
          maxTimeStartByWell.push(maxItem);
        }
      });
      const minTreatment = minBy(minTimeStartByWell, 'timeStart');
      const maxTreatment = maxBy(maxTimeStartByWell, 'timeStart');
      if (minTreatment && maxTreatment) {
        const calcLocalTime = (timeStart: number, tzTimeZone: string) => {
          const offsetTime = Helper.getTimezoneOffset(timeStart, tzTimeZone);
          return timeStart + offsetTime * 60;
        };
        result.startTime = calcLocalTime(minTreatment.timeStart, minTreatment.tzTimeZone);
        result.endTime = calcLocalTime(maxTreatment.timeStart, maxTreatment.tzTimeZone);
        if (toISOString) {
          result.startTime = new Date(result.startTime * 1000).toISOString();
          result.endTime = new Date(result.endTime * 1000).toISOString();
        }
      }
    }
    return result;
  }

  findTimeRangeData(wellList: IWellItemPaging[], isMin) {
    // find data ranges by treatment's timeStart
    let timeRange; // in sec
    const dateRange = this.findDateRangeData(wellList);
    if (dateRange.startTime && dateRange.endTime) {
      timeRange = dateRange.endTime - dateRange.startTime;
      if (isMin) {
        timeRange = timeRange / 60;
      }
    }
    return timeRange;
  }

  handleWellTreatmentName(originWellList: any[]): any[] {
    if (originWellList) {
      const cloneWellList = cloneDeep(originWellList);

      cloneWellList.forEach(well => {
        if (well.treatmentList) {
          well.treatmentList.forEach(item => {
            if (item.treatmentNumber > 0) {
              item.treatmentName = `Treatment ${item.treatmentNumber}`;
            }
          });
        }
      });

      return cloneWellList;
    }
    return [];
  }

  filterWellTreatmentsByDateRange(originWellList, startTime, endTime, refreshTreatmentSelected: boolean = true) {
    // load all treatment in start-end time
    if (!isEmpty(originWellList)) {
      if (startTime && endTime) {
        const cloneWellList = cloneDeep(originWellList);
        cloneWellList.forEach(well => {
          // filter selected treatments in date range
          const startDateTime = typeof startTime === 'string' ? new Date(startTime).getTime() : new Date(startTime.set('hour', 0)).getTime();
          const endDateTime = typeof endTime === 'string' ? new Date(endTime).getTime() : new Date(endTime.set('hour', 0)).getTime();
          const offsetTime = Helper.getTimezoneOffset(startDateTime, well.tzTimeZone);
          const startDateFilter = startDateTime / 1000 - offsetTime * 60;
          const endDateFilter = endDateTime / 1000 + 24 * 60 * 60 - offsetTime * 60;
          const treatmentInDateRange = well.treatmentList.filter(t => {
            return t.timeStart >= startDateFilter && t.timeStart <= endDateFilter;
          });
          if (!!refreshTreatmentSelected) {
            this.refreshTreatmentSelected(well, treatmentInDateRange);
          } else {
            this.handleTreatmentSelected(well, treatmentInDateRange);
          }
        });

        return cloneWellList;
      } else { // Last Days = None (Manual)
        return this.getWellTratmentsWhenManualFilterLastDay(originWellList);
      }
    }
    return [];
  }

  refreshTreatmentSelected(well, treatmentList: any[]) {
    if (treatmentList) {
      well.treatment = treatmentList.map(i => {
        return {
          id: i.id,
          text: i.treatmentNumber > 0 ? `${i.treatmentNumber.toString()}` : i.treatmentName,
        };
      });
      well.selectedTreatmentList = treatmentList.map(i => {
        return {
          id: i.id,
          text: i.treatmentNumber.toString(),
          treatmentName: i.treatmentName,
        };
      });
    } else {
      well.treatment = [];
      well.selectedTreatmentList = [];
    }
  }

  handleTreatmentSelected(well, treatmentList: any[]) {
    if (treatmentList) {
      // filter selected treatments label
      if (well.treatment) {
        well.treatment = well.treatment.filter(item => {
          return treatmentList.findIndex(x => x.id === item.id) !== -1;
        });
      } else {
        well.treatment = [];
      }
      // filter selected treatments list
      if (well.selectedTreatmentList) {
        well.selectedTreatmentList = well.selectedTreatmentList.filter(item => {
          return well.treatment.findIndex(x => x.id === item.id) !== -1;
        });
      } else {
        well.selectedTreatmentList = [];
      }
    } else {
      well.treatment = [];
      well.selectedTreatmentList = [];
    }
  }

  getWellTratmentsWhenManualFilterLastDay(originWellList) {
    if (!originWellList) {
      return [];
    }
    const cloneWellList = cloneDeep(originWellList);

    cloneWellList.forEach(well => {
      this.handleTreatmentSelected(well, well.treatmentList);
    });

    return cloneWellList;
  }

  populateAliasInfo(wellList: IWellOptions[]) {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');
    if (!isEmpty(wellList)) {
      wellList.forEach((well, index) => {
        if (!well.aliasName) {
          well.aliasName = alphabet[index];
        }
        well.wellColor = well.wellColor ? well.wellColor : (DefaultColor[index] || Helper.randomHexColor());
      });
    }
    return wellList;
  }

  getChartDataByGroupTreatments(
    wellId: number,
    treatmentIds: number[],
    channelsMap: { realtimeItems: any[], fracproItems: any[] },
    wellTimeZoneOffset: number[],
    dataStoreService: CompareDataTreatmentsStored,
    isFilterTreatment?: boolean,
    isUnsetLocalData?: boolean,
    requestOptions?: ITreatmentChartRequestOption
  ): Observable<any> {
    if (wellId && !isEmpty(treatmentIds) && channelsMap && (!isEmpty(channelsMap.fracproItems) || !isEmpty(channelsMap.realtimeItems))) {
      let filteredIds = cloneDeep(treatmentIds);
      if (isFilterTreatment) {
        filteredIds = dataStoreService.filterNotExistedDataTreatmentIds(treatmentIds);
      }
      // grouping ids in block 10 items
      const groupIds = [];
      while (filteredIds.length > 0) {
        const ids = filteredIds.splice(0, 10);
        groupIds.push(ids);
      }
      if (groupIds.length > 0) {
        const requests = groupIds.map(ids => {
          const requestBody = {
            wellId,
            treatmentIds: ids,
            realtimeItems: channelsMap.realtimeItems,
            fracproItems: channelsMap.fracproItems,
            flowPathType: 0
          };
          return this.chartsService.getSingleWellMultiTreatmeantChartData(requestBody, requestOptions);
        });

        return forkJoin(requests)
          .pipe(map(res => {
            let data = [];
            res.forEach(item => {
              data = data.concat(item.result);
            });
            return data;
          }))
          .pipe(map(data => {
            // update data treatments in local service
            const n = data.length;
            for (let index = 0; index < n; index++) {
              const dataTreatment = data[index];
              // this.baseChartService.updateTimestampByTimezoneOffset(dataTreatment.realtimeData[0], wellTimeZoneOffset[index]);
              // this.baseChartService.updateTimestampByTimezoneOffset(dataTreatment.fracproData[0], wellTimeZoneOffset[index]);
              if (!isUnsetLocalData) {
                dataStoreService.setDataByTreatmentId(dataTreatment.treatmentId, dataTreatment);
              }
            }
            // get data treatments from local service
            if (!isUnsetLocalData) {
              const allDataByTreatments = dataStoreService.getArrayDataTreatments(treatmentIds);
              return allDataByTreatments;
            } else {
              return data;
            }
          }));
      } else {
        // get data treatments from local service
        const allDataByTreatments = dataStoreService.getArrayDataTreatments(treatmentIds);
        return of(allDataByTreatments);
      }
    } else {
      return of([]);
    }
  }

}
